import SunCalc from "suncalc";
import {
  CHOGHADIYA_INFO,
  DAY_SEQUENCES,
  NIGHT_SEQUENCES,
  WEEKDAY_NAMES,
} from "./choghadiyaData";

/**
 * Returns the local-date (Y, M, D) of `dateObj` AS OBSERVED in `timezone`.
 * Falls back to browser-local date if no timezone provided.
 */
function localYMD(dateObj, timezone) {
  if (!timezone) return [dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()];
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  const parts = fmt.formatToParts(dateObj);
  const y = parseInt(parts.find((p) => p.type === "year").value, 10);
  const m = parseInt(parts.find((p) => p.type === "month").value, 10) - 1;
  const d = parseInt(parts.find((p) => p.type === "day").value, 10);
  return [y, m, d];
}

/**
 * Compute Choghadiya for given lat/lng/date.
 * `timezone` (optional, IANA name) ensures the "calendar day" is the LOCAL day at the
 * location — not the browser/server day. Critical for evenings & overnight cases.
 */
export function computeChoghadiya(lat, lng, dateObj = new Date(), timezone) {
  // Local day at the LOCATION. Using UTC noon of that local date keeps SunCalc's
  // astronomy calculations correct (sunrise / sunset only need the calendar day).
  const [y, m, d] = localYMD(dateObj, timezone);
  const baseDate = new Date(Date.UTC(y, m, d, 12, 0, 0));
  const t = SunCalc.getTimes(baseDate, lat, lng);
  let sunrise = t.sunrise;
  let sunset = t.sunset;

  // If the user is BEFORE today's sunrise (early-morning at this location),
  // the active choghadiya belongs to YESTERDAY's day-night span, so roll back.
  //
  // BUT this rollback is only meaningful when `dateObj` represents "now". For
  // calendar-picked dates (typically midnight of some chosen day) we want the
  // exact calendar day's cycle, no rollback. We detect "is this `now`-ish?"
  // by checking whether `dateObj` is within ±90 seconds of the real current
  // time — that covers `new Date()` callers and excludes calendar selections.
  const isLiveQuery = Math.abs(dateObj.getTime() - Date.now()) < 90 * 1000;
  if (isLiveQuery && dateObj < sunrise) {
    const prevDay = new Date(baseDate);
    prevDay.setUTCDate(prevDay.getUTCDate() - 1);
    const tp = SunCalc.getTimes(prevDay, lat, lng);
    sunrise = tp.sunrise;
    sunset = tp.sunset;
  }

  // Next day sunrise (for night choghadiya end)
  const nextDayBase = new Date(sunrise);
  nextDayBase.setUTCDate(nextDayBase.getUTCDate() + 1);
  const tn = SunCalc.getTimes(nextDayBase, lat, lng);
  const nextSunrise = tn.sunrise;

  const weekday = sunrise.getDay(); // 0..6 Sun..Sat
  const daySeq = DAY_SEQUENCES[weekday];
  const nightSeq = NIGHT_SEQUENCES[weekday];

  const dayDuration = sunset - sunrise;
  const nightDuration = nextSunrise - sunset;
  const daySlot = dayDuration / 8;
  const nightSlot = nightDuration / 8;

  const build = (seq, start, slotMs, kind) =>
    seq.map((key, i) => {
      const s = new Date(start.getTime() + i * slotMs);
      const e = new Date(start.getTime() + (i + 1) * slotMs);
      const info = CHOGHADIYA_INFO[key];
      return {
        index: i,
        kind,
        key,
        ...info,
        start: s,
        end: e,
      };
    });

  return {
    date: baseDate,
    sunrise,
    sunset,
    nextSunrise,
    weekday,
    weekdayName: WEEKDAY_NAMES[weekday],
    day: build(daySeq, sunrise, daySlot, "day"),
    night: build(nightSeq, sunset, nightSlot, "night"),
  };
}

export function findActiveSlot(choghadiya, now = new Date()) {
  const all = [...choghadiya.day, ...choghadiya.night];
  return all.find((s) => now >= s.start && now < s.end) || null;
}

export function findNextAuspiciousSlot(choghadiya, now = new Date()) {
  const all = [...choghadiya.day, ...choghadiya.night];
  return all.find((s) => s.start > now && s.nature === "auspicious") || null;
}

export function formatTime(date, tz = undefined) {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date);
}

export function formatRange(s, e, tz = undefined) {
  return `${formatTime(s, tz)} — ${formatTime(e, tz)}`;
}

export function diffHMS(target, now = new Date()) {
  const ms = Math.max(0, target - now);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { h, m, s, total: ms };
}
